import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { type SxProps, type Theme } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";

import { MESSAGE_CUSTOM_TYPES } from "../constants";
import type { AdminChatMessage } from "../types";
import { parseShiftInviteMessageData } from "../utils/parseShiftInviteMessage";
import { GroupShiftInviteRequestMessage } from "./GroupShiftInviteRequestMessage";
import { ShiftInviteRequestMessage } from "./ShiftInviteRequestMessage";

interface ShiftInviteMessageRouterProps {
  message: AdminChatMessage;
  sx?: SxProps<Theme>;
}

/**
 * Routes shift invite admin messages to the appropriate component.
 * Returns null if the message should not be displayed.
 */
export function ShiftInviteMessageRouter(
  props: ShiftInviteMessageRouterProps
): React.ReactElement | null {
  const { message, sx } = props;

  if (message.customType === MESSAGE_CUSTOM_TYPES.GROUP_SHIFT_INVITE_REQUEST) {
    return <GroupShiftInviteRequestMessage message={message} sx={sx} />;
  }

  if (message.customType !== MESSAGE_CUSTOM_TYPES.SHIFT_INVITE_REQUEST) {
    return null;
  }

  const { shiftInviteId, status } = parseShiftInviteMessageData(message.data);

  if (!isDefined(shiftInviteId) || !isDefined(status)) {
    logError(APP_V2_APP_EVENTS.SHIFT_INVITE_REQUEST_MESSAGE_RENDER_FAILURE, {
      error: new Error("Shift invite ID and status are required"),
      metadata: { messageId: message.messageId },
    });
    return null;
  }

  // Don't render deleted shift invites
  if (status === ShiftInviteStatus.DELETED) {
    return null;
  }

  return <ShiftInviteRequestMessage shiftInviteId={shiftInviteId} sx={sx} />;
}
